import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * High precision interval timer using requestAnimationFrame + performance.now().
 * Never uses setInterval.
 *
 * @param {Array} phases  flat list of phases (see generatePhases)
 * @param {Object} opts   { autoStart, onPhaseChange, onFinish, onTick }
 */
export function useTimer(phases, opts = {}) {
  const { autoStart = false, onPhaseChange, onFinish, onTick } = opts;

  const [phaseIndex, setPhaseIndex] = useState(0);
  const [remaining, setRemaining] = useState(phases[0] ? phases[0].duration : 0);
  const [status, setStatus] = useState('idle'); // idle | running | paused | finished

  const rafRef = useRef(null);
  const startRef = useRef(0); // performance.now at (adjusted) phase start
  const idxRef = useRef(0);
  const phasesRef = useRef(phases);
  phasesRef.current = phases;

  const cbRef = useRef({});
  cbRef.current = { onPhaseChange, onFinish, onTick };

  const stopLoop = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  };

  const loop = useCallback(() => {
    const now = performance.now();
    const list = phasesRef.current;
    const cur = list[idxRef.current];
    if (!cur) return;

    const elapsed = (now - startRef.current) / 1000;
    let rem = cur.duration - elapsed;

    if (rem <= 0) {
      const nextIdx = idxRef.current + 1;
      if (nextIdx >= list.length) {
        idxRef.current = nextIdx;
        setRemaining(0);
        setStatus('finished');
        stopLoop();
        cbRef.current.onFinish && cbRef.current.onFinish(cur);
        return;
      }
      idxRef.current = nextIdx;
      const nextPhase = list[nextIdx];
      startRef.current = now;
      setPhaseIndex(nextIdx);
      setRemaining(nextPhase.duration);
      cbRef.current.onPhaseChange &&
        cbRef.current.onPhaseChange(nextPhase, cur);
      rem = nextPhase.duration;
    } else {
      setRemaining(rem);
      cbRef.current.onTick && cbRef.current.onTick(rem, cur);
    }

    rafRef.current = requestAnimationFrame(loop);
  }, []);

  const goToPhase = useCallback(
    (idx, keepStatus = true) => {
      const list = phasesRef.current;
      const clamped = Math.max(0, Math.min(idx, list.length - 1));
      idxRef.current = clamped;
      startRef.current = performance.now();
      setPhaseIndex(clamped);
      setRemaining(list[clamped].duration);
      if (!keepStatus) return;
      cbRef.current.onPhaseChange &&
        cbRef.current.onPhaseChange(list[clamped], null);
    },
    []
  );

  const start = useCallback(() => {
    stopLoop();
    idxRef.current = 0;
    startRef.current = performance.now();
    setPhaseIndex(0);
    setRemaining(phasesRef.current[0] ? phasesRef.current[0].duration : 0);
    setStatus('running');
    cbRef.current.onPhaseChange &&
      cbRef.current.onPhaseChange(phasesRef.current[0], null);
    rafRef.current = requestAnimationFrame(loop);
  }, [loop]);

  const pause = useCallback(() => {
    if (status !== 'running') return;
    stopLoop();
    // preserve elapsed by freezing remaining -> recompute on resume
    const now = performance.now();
    const elapsed = (now - startRef.current) / 1000;
    const cur = phasesRef.current[idxRef.current];
    startRef.current = now - Math.min(elapsed, cur.duration) * 1000;
    setStatus('paused');
  }, [status]);

  const resume = useCallback(() => {
    if (status !== 'paused') return;
    // recalc start so remaining continues from where it froze
    const cur = phasesRef.current[idxRef.current];
    const rem = remaining;
    startRef.current = performance.now() - (cur.duration - rem) * 1000;
    setStatus('running');
    rafRef.current = requestAnimationFrame(loop);
  }, [status, remaining, loop]);

  const skip = useCallback(() => {
    const list = phasesRef.current;
    const nextIdx = idxRef.current + 1;
    if (nextIdx >= list.length) {
      idxRef.current = list.length;
      stopLoop();
      setStatus('finished');
      setRemaining(0);
      cbRef.current.onFinish &&
        cbRef.current.onFinish(list[idxRef.current - 1]);
      return;
    }
    goToPhase(nextIdx);
  }, [goToPhase]);

  const previous = useCallback(() => {
    goToPhase(idxRef.current - 1);
  }, [goToPhase]);

  const stop = useCallback(() => {
    stopLoop();
    idxRef.current = 0;
    setPhaseIndex(0);
    setRemaining(phasesRef.current[0] ? phasesRef.current[0].duration : 0);
    setStatus('idle');
  }, []);

  const restart = useCallback(() => {
    start();
  }, [start]);

  useEffect(() => {
    if (autoStart) start();
    return () => stopLoop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = phases[phaseIndex] || null;
  const progress = current ? 1 - remaining / current.duration : 0;

  return {
    phase: current,
    phaseIndex,
    totalPhases: phases.length,
    remaining,
    remainingSec: Math.ceil(remaining - 0.0001),
    progress: Math.max(0, Math.min(1, progress)),
    status,
    isRunning: status === 'running',
    isPaused: status === 'paused',
    isFinished: status === 'finished',
    controls: { start, pause, resume, skip, previous, stop, restart },
  };
}
